import { isHealthyContractStatus, normalizeContractStatus } from "@/utils/contractStatus";

/**
 * Čista logika stranice Ugovori (bez Vue-a, pa se može provjeriti u Node-u): redovi pregleda ugovora, redovi radnog spiska
 * cjenovnika, brojanje po filteru i redoslijed po hitnosti. Oblik odgovora je iz docs/specs/SPEC-FRONTEND-ugovori-usluge-cjenovnik.md,
 * dopunjen onim što backend stvarno šalje (provjera 30.10.2026: `vazi_od` i `contract_date` u /contracts/expiring,
 * `contract_number`, `ugovor_vazi_do` i `potvrdjeno` u /pricing/pending-verification).
 */

const text = (value) => (value === null || value === undefined ? "" : String(value));

/** "VAŽI" i "BEZ ROKA" su zdravi, ostalo je istekao / uskoro ističe */
export const contractKind = (status) => {
  const normalized = normalizeContractStatus(status);
  if (normalized === "ISTEKAO") return "expired";
  if (normalized === "USKORO ISTIČE") return "expiring";
  return isHealthyContractStatus(normalized) ? "valid" : "other";
};

const RANK = { expired: 0, expiring: 1, valid: 2, other: 2, none: 3 };

/** Hitnije prvo: istekli (najduže istekao prvi), pa uskoro ističu (najmanje dana prvo), pa ostali kako ih je backend poslao, a bez ugovora na kraju. */
export const sortOverview = (rows) =>
  rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const byKind = RANK[a.row.kind] - RANK[b.row.kind];
      if (byKind) return byKind;
      if (a.row.kind === "expired" || a.row.kind === "expiring") {
        const days = (a.row.days === null ? 0 : a.row.days) - (b.row.days === null ? 0 : b.row.days);
        if (days) return days;
      }
      return a.index - b.index;
    })
    .map((item) => item.row);

/** Redovi pregleda: jedan red po restoranu sa ugovorom (backend vraća samo najnoviji) i redovi restorana bez ugovora. */
export const buildOverviewRows = (contracts = [], withoutContract = []) => {
  const withContracts = contracts.map((contract) => ({
    key: `c-${contract.contract_id}`,
    kind: contractKind(contract.status),
    hasContract: true,
    restaurantId: contract.restaurant_id,
    name: text(contract.naziv),
    status: normalizeContractStatus(contract.status),
    contractNumber: text(contract.contract_number),
    contractDate: contract.contract_date || null,
    // /contracts/expiring šalje `vazi_od`; `valid_from` je ostao za slučaj da ga backend preimenuje
    from: contract.vazi_od || contract.valid_from || null,
    to: contract.vazi_do || null,
    days: contract.dana_do_isteka === undefined ? null : contract.dana_do_isteka,
    services: contract.broj_usluga === undefined ? null : contract.broj_usluga,
    opis: text(contract.opis),
    raw: contract,
  }));
  const without = withoutContract.map((item) => ({
    key: `n-${item.restaurant_id}`,
    kind: "none",
    hasContract: false,
    restaurantId: item.restaurant_id,
    name: text(item.naziv),
    status: "BEZ_UGOVORA",
    contractNumber: "",
    contractDate: null,
    from: null,
    to: null,
    days: null,
    services: null,
    opis: "",
    raw: item,
  }));
  return sortOverview([...withContracts, ...without]);
};

export const countOverview = (rows) => {
  const counts = { all: rows.length, expired: 0, expiring: 0, valid: 0, none: 0 };
  rows.forEach((row) => {
    if (row.kind === "none") counts.none += 1;
    else if (row.kind === "expired") counts.expired += 1;
    else if (row.kind === "expiring") counts.expiring += 1;
    else counts.valid += 1;
  });
  return counts;
};

export const filterOverview = (rows, kind) => {
  if (!kind || kind === "all") return rows;
  if (kind === "valid") return rows.filter((row) => row.kind === "valid" || row.kind === "other");
  return rows.filter((row) => row.kind === kind);
};

// ---------------------------------------------------------------- radni spisak cjenovnika

/** statusi iz /pricing/pending-verification (sa dijakritikom, kako ih backend šalje od 12.09) -> ključ filtera */
const PENDING_KEYS = {
  "NEDOSTAJE CJENOVNIK": "missing",
  "CJENOVNIK BEZ IZNOSA": "no_amount",
  "CJENOVNIK NIJE POTVRĐEN": "unverified",
  "UGOVOR ISTEKAO": "contract_expired",
  "UGOVOR USKORO ISTIČE": "contract_expiring",
  OK: "ok",
};

export const pendingKey = (status) => PENDING_KEYS[status] || "other";

/** "nacin_naplate" stiže kao mala slova bez dijakritika ("mjesecno", "procenat", "po obroku"); vraća vrstu naplate (1-4) ili 0 */
const PAY_WORDS = [
  [/^mj?e[sš]/, 1],
  [/^procenat|^posto|%/, 2],
  [/obrok/, 3],
  [/narud/, 4],
];

export const payTypeFromWord = (word) => {
  const value = text(word).trim().toLowerCase();
  if (!value) return 0;
  const hit = PAY_WORDS.find(([pattern]) => pattern.test(value));
  return hit ? hit[1] : 0;
};

export const buildPendingRows = (items = []) =>
  items.map((item) => ({
    key: `p-${item.restaurant_id}`,
    group: pendingKey(item.status),
    restaurantId: item.restaurant_id,
    name: text(item.naziv),
    kind: text(item.tip).toLowerCase(),
    status: text(item.status),
    payType: payTypeFromWord(item.nacin_naplate),
    payWord: text(item.nacin_naplate),
    amount: item.iznos === null || item.iznos === undefined || item.iznos === "" ? null : Number(item.iznos),
    opis: text(item.opis),
    validFrom: item.vazi_od || null,
    contractNumber: text(item.contract_number),
    contractTo: item.ugovor_vazi_do || null,
    raw: item,
  }));

/** Brojke po grupi. Redoslijed grupa je po hitnosti (nema podataka = nema brojke u filteru, ali ostaje u spisku `all`). */
export const countPending = (rows) => {
  const counts = { all: rows.length };
  rows.forEach((row) => {
    counts[row.group] = (counts[row.group] || 0) + 1;
  });
  return counts;
};

export const filterPending = (rows, group) => (!group || group === "all" ? rows : rows.filter((row) => row.group === group));

/** Spisak se otvara na ugovoru kad je problem sam ugovor, a na uslugama i cijenama kad je problem cjenovnik. */
export const pendingOpensContracts = (group) => group === "contract_expired" || group === "contract_expiring";

/**
 * Ugovor u istoriji restorana: "zamijenjen" je onaj koji je zatvorio noviji (lista je sortirana, najnoviji prvi),
 * pa za njega nema alarma "istekao" ni "i dalje se obračunava" (po njemu se računa samo do datuma zatvaranja).
 */
export const contractRole = (contract, index) => {
  const kind = contractKind(contract.status);
  if (index > 0 && kind === "expired") return "replaced";
  if (kind === "expired") return "expired-current";
  return kind;
};
